import type { StatusTone } from '../contracts/common';
import type {
  TaskAiState,
  TaskDueFilter,
  TaskOrigin,
  TaskPriority,
  TaskProposalSource,
  TaskProposalStatus,
  TaskScope,
  TaskSort,
  TaskStatus,
} from '../contracts/work';

/** Filtr terminu (lista zadań). */
export const TASK_DUE_FILTER_LABELS: Record<TaskDueFilter, string> = {
  overdue: 'Po terminie',
  today: 'Dziś',
  week: 'Ten tydzień',
  none: 'Bez terminu',
};

export const TASK_SORT_LABELS: Record<TaskSort, string> = {
  due: 'Termin',
  priority: 'Priorytet',
  updated: 'Ostatnio zmienione',
  created: 'Najnowsze',
  position: 'Kolejność z tablicy',
};

export const TASK_SCOPE_LABELS: Record<TaskScope, string> = {
  mine: 'Moje',
  team: 'Zespół',
};

/** Priorytet w UI: kolor tylko dla pilnych i wysokich (DESIGN §2.3). */
export const TASK_PRIORITY_TONES: Record<TaskPriority, StatusTone> = {
  low: 'neutral',
  normal: 'neutral',
  high: 'warning',
  urgent: 'danger',
};

/** Pola w historii zmian zadania. */
export const TASK_FIELD_LABELS: Record<string, string> = {
  title: 'Tytuł',
  descriptionMd: 'Opis',
  clientId: 'Klient',
  projectId: 'Projekt',
  serviceId: 'Usługa',
  assigneeUserId: 'Wykonawca',
  status: 'Status',
  priority: 'Priorytet',
  dueDate: 'Termin',
};

/** Zdarzenia w historii zadania (audit_log, object_type = 'task'). */
export const TASK_HISTORY_ACTION_LABELS: Record<string, string> = {
  'task.create': 'Utworzono zadanie',
  'task.update': 'Zmieniono zadanie',
  'task.move': 'Zmieniono status',
  'task.archive': 'Zarchiwizowano zadanie',
  'task.restore': 'Przywrócono zadanie',
  'task.checklist.add': 'Dodano pozycję checklisty',
  'task.checklist.done': 'Odhaczono pozycję checklisty',
  'task.checklist.undone': 'Cofnięto odhaczenie pozycji',
  'task.checklist.update': 'Zmieniono treść pozycji checklisty',
  'task.checklist.remove': 'Usunięto pozycję checklisty',
  'task.comment': 'Komentarz',
};

/** Kolumny Kanban i statusy listy (MVP §6, Figma 04 Zadania). */
export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  request: 'Zgłoszenie',
  todo: 'Do zrobienia',
  in_progress: 'W toku',
  blocked: 'Zablokowane',
  review: 'Do odbioru',
  done: 'Gotowe',
};

export const TASK_STATUS_TONES: Record<TaskStatus, StatusTone> = {
  request: 'accent',
  todo: 'neutral',
  in_progress: 'accent',
  blocked: 'danger',
  review: 'warning',
  done: 'success',
};

export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: 'Niski',
  normal: 'Normalny',
  high: 'Wysoki',
  urgent: 'Pilny',
};

export const TASK_ORIGIN_LABELS: Record<TaskOrigin, string> = {
  manual: 'Ręcznie',
  message: 'Z wiadomości',
  meeting: 'Ze spotkania',
  ai: 'Od AI',
  mcp: 'Przez MCP',
  proposal: 'Z propozycji',
  client_portal: 'Z portalu klienta',
};

/** Nagłówek kolumny Kanbanu dla statusu `request` (17.10.2026); lista i karta używają `TASK_STATUS_LABELS.request`. */
export const TASK_REQUEST_COLUMN_LABEL = 'Zgłoszenia od klientów';
/** Karta zadania: rozmowa widoczna dla klienta (obok komentarzy wewnętrznych). */
export const TASK_CLIENT_CONVERSATION_LABEL = 'Rozmowa z klientem';

/**
 * Stan AI w karcie zadania (DESIGN §2.5). `none` nie jest pokazywany. `review` = „Wynik AI czeka” (DECISIONS 2026-10-06
 * „Rozstrzygnięcia pytań z Fali 6”), żeby nie mylił się ze statusem zadania „Do odbioru” (kolumna Kanbanu).
 */
export const TASK_AI_STATE_LABELS: Record<TaskAiState, string> = {
  none: 'Bez AI',
  proposed: 'AI proponuje',
  running: 'AI pracuje',
  review: 'Wynik AI czeka',
  waiting_approval: 'Czeka na akceptację',
  done: 'Gotowe',
};

export const TASK_AI_STATE_TONES: Record<TaskAiState, StatusTone> = {
  none: 'neutral',
  proposed: 'accent',
  running: 'accent',
  review: 'warning',
  waiting_approval: 'accent',
  done: 'success',
};

export const TASK_PROPOSAL_SOURCE_LABELS: Record<TaskProposalSource, string> = {
  meeting: 'Spotkanie',
  message: 'Wiadomość',
  ai: 'AI',
};

export const TASK_PROPOSAL_STATUS_LABELS: Record<TaskProposalStatus, string> = {
  proposed: 'Propozycja',
  accepted: 'Dodane',
  rejected: 'Odrzucone',
};
